import React, { useEffect, useState } from 'react';
import './css/QuestionnaireSuccessModal.css';

interface QuestionnaireSuccessModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAddToJob: () => void;
  onSetAsDefault?: () => void;
}

const QuestionnaireSuccessModal: React.FC<QuestionnaireSuccessModalProps> = ({
  isOpen,
  onClose,
  onAddToJob,
  onSetAsDefault,
}) => {
  const [isClosing, setIsClosing] = useState(false);
  const [shouldRender, setShouldRender] = useState(isOpen);
  const [skipAnimation, setSkipAnimation] = useState(false);

  // Handle modal visibility with animation
  useEffect(() => {
    if (isOpen) {
      setShouldRender(true);
      setIsClosing(false);
      setSkipAnimation(false);
    } else if (shouldRender) {
      if (skipAnimation) {
        // Immediately close without animation
        setShouldRender(false);
        setIsClosing(false);
        setSkipAnimation(false);
      } else {
        // Start closing animation
        setIsClosing(true);
        // Wait for animation to complete before removing from DOM
        const timer = setTimeout(() => {
          setShouldRender(false);
          setIsClosing(false);
        }, 300); // Match animation duration
        return () => clearTimeout(timer);
      }
    }
  }, [isOpen, shouldRender, skipAnimation]);

  // Handle ESC key press
  useEffect(() => {
    if (!isOpen) return;

    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        handleClose();
      }
    };

    document.addEventListener('keydown', handleEscKey);
    return () => {
      document.removeEventListener('keydown', handleEscKey);
    };
  }, [isOpen]);

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      onClose();
    }, 300); // Match animation duration
  };

  const handleAddToJobClick = () => {
    // Skip animation for Add to Job action
    setSkipAnimation(true);
    onAddToJob();
  };

  const handleSetAsDefaultClick = () => {
    // Skip animation so SetDefaultModal isn't hidden behind this overlay
    setSkipAnimation(true);
    if (onSetAsDefault) onSetAsDefault();
  };

  if (!shouldRender) return null;

  // Handle click outside modal
  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  return (
    <div 
      className={`questionnaire-success-modal-overlay ${isClosing ? 'closing' : ''}`} 
      onClick={handleOverlayClick}
    >
      <div className={`questionnaire-success-modal ${isClosing ? 'closing' : ''}`}>
        {/* Header with Title and Close Button */}
        <div className="questionnaire-success-modal__header">
          <h1 className="questionnaire-success-modal__header-title">
            Questionnaire Successfully Made
          </h1>
          <button 
            className="questionnaire-success-modal__close" 
            onClick={handleClose}
            aria-label="Close modal"
          >
            <i className="fal fa-xmark" style={{ fontSize: '1.2rem', color: '#495057' }}></i>
          </button>
        </div>

        {/* Step Progress Indicator */}
        <div className="questionnaire-success-modal__progress">
          <div className="questionnaire-success-modal__step-labels">
            <div className="questionnaire-success-modal__step-label questionnaire-success-modal__step-label--completed">
              <span className="questionnaire-success-modal__step-number">Step 1</span>
              <span className="questionnaire-success-modal__step-text">Create Questionnaire</span>
            </div>
            <div className="questionnaire-success-modal__step-label questionnaire-success-modal__step-label--active">
              <span className="questionnaire-success-modal__step-number">Step 2</span>
              <span className="questionnaire-success-modal__step-text">Add Questionnaire to Job</span>
            </div>
          </div>
          <img 
            src="/svgs/1-2.svg" 
            alt="Progress: Step 1 of 2 completed" 
            className="questionnaire-success-modal__progress-svg"
          />
        </div>

        {/* Trophy Illustration */}
        <div className="questionnaire-success-modal__illustration">
          <img 
            src="/svgs/trophy.svg" 
            alt="Success Trophy" 
            className="questionnaire-success-modal__trophy"
          />
        </div>

        {/* Message */}
        <div className="questionnaire-success-modal__content">
          <h2 className="questionnaire-success-modal__title">
            Questionnaire created. Now let's put it to work!
          </h2>
          <p className="questionnaire-success-modal__description">
            Add it to a job now to complete setup and start collecting <br className="questionnaire-success-modal__br-desktop" /> interview responses right away.
          </p>
        </div>

        {/* Actions */}
        <div className="questionnaire-success-modal__actions">
          <button
            className="questionnaire-success-modal__button questionnaire-success-modal__button--primary"
            onClick={handleAddToJobClick}
          >
            Add to Job
          </button>
          {onSetAsDefault && (
            <button
              className="questionnaire-success-modal__button questionnaire-success-modal__button--secondary"
              onClick={handleSetAsDefaultClick}
            >
              Set as Default
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default QuestionnaireSuccessModal;
